import { useCallback, useState } from 'react'
import { PageData } from '@/api/Pagination/Types'
import { RewardProgramDto } from '@/api/Trading-Accounts/Types/RewardsHubDto'
import {
  createGridState,
  useGridFilter,
  useGridState,
  useGridStatusState,
} from '@/features/DataGrid'
import { isGoBack } from '@/lib/react-router-dom'
import { CompositeFilterDescriptor, GridFilterActionState } from '@/store/grid'
import { useApiClient } from '@/utils/api/ApiClient'
import { TradingAccountsApiClient } from '@/utils/api/tradingAccountsApi'
import { buildQuery, buildSortQuery } from '@/utils/QueryHelpers'
import { TableStateContextData } from '@/utils/TableStateContext'
import { SortDescriptor } from '@progress/kendo-data-query'
import { GridSortChangeEvent } from '@progress/kendo-react-grid'

export const useRewardProgramsFiltering = () => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [data, setData] = useState<RewardProgramDto[]>([])
  const { gridState, gridActions } = useGridState()
  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      const filterDescriptor = filter ? filter : gridState.filter
      return buildQuery(filterDescriptor) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(sortDescriptors ?? gridState.sort)
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSort(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await tradingAccountsApiClient.getRewardPrograms({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setData(response.items.map((x) => Object.assign({ selected: false }, x)))
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<RewardProgramDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )

    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    gridStatusActions.setFetching(true)

    const response = await tradingAccountsApiClient.getRewardPrograms({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })

    gridStatusActions.setFetching(false)

    gridActions.setGridFilters({
      total: response.totalResults,
    })

    if (response?.items) {
      setData(response.items)
    }
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    const sort = event.sort
    const sortQuery = getSort(sort)
    const searchQuery = getQuery()

    gridStatusActions.setFetching(true)

    const response = await tradingAccountsApiClient.getRewardPrograms({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })

    gridStatusActions.setFetching(false)

    gridActions.setGridFilters({
      sort,
      total: response.totalResults,
    })

    if (response?.items) {
      setData(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page

    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await tradingAccountsApiClient.getRewardPrograms({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })

    gridActions.setGridFilters({
      total: response.totalResults,
    })

    if (response?.items) {
      setData(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await tradingAccountsApiClient.getRewardPrograms({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })

    if (response?.items) {
      setData(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  return {
    tableData,
    setTableData,
    data,
    getSort,
    setData,
    gridState,
    fetchGrid,
    handleSkipCountChange,
    handleSortChange,
    handleGridPageChange,
    handlePageSizeChange,
    gridStatusState,
    gridStatusActions,
    gridActions,
    filterFetch,
    getQuery,
  }
}
